import { ProductInfoForKey, ProductKey, ProductStepLimit } from "libs/product/src/lib/models/product.model";
import { OrcadentPackage, OrcadentPackageType, OrcadentTreatmentPackages } from "../models";

export class OrcadentPackagesService {

	/**
	 * Checks whether the treatment is running depending on the state of the packages
	 * @param packages
	 * @returns true, if packages have already been ordered
	 */
	public static isTreatmentRunning( packages: OrcadentTreatmentPackages ) : boolean {
		if(!packages){
			return false;
		}
		return !!packages.active || packages.closed.length > 0;
	}

	/**
	 * Moves a package from the 'pending' array to the 'active' "state"
	 * This is only possible if pending packages exist and if there is not
	 * already an active package
	 * @param packages
	 * @returns the updated packages structure
	 */
	public static advanceNextPackage( packages: OrcadentTreatmentPackages ) : OrcadentTreatmentPackages {
		if (!packages){
			return null;
		}
		if( packages.pending.length === 0){
			return packages;
		}
		if( !!packages.active ){
			return packages;
		}
		const nextPackage = packages.pending.shift();

		return {
			initialPlannedSteps: packages.initialPlannedSteps,
			pending: packages.pending,
			active: nextPackage,
			closed: packages.closed,
		}

	}

	/**
	 * Moves an 'active' package to the 'closed' array
	 * This is only possible if an active packages exists
	 * @param packages
	 * @returns the updated packages structure
	 */
	public static closeActivePackage( packages: OrcadentTreatmentPackages ) : OrcadentTreatmentPackages {
		if (!packages){
			return null;
		}
		if( !packages.active){
			return packages;
		}
		return {
			initialPlannedSteps: packages.initialPlannedSteps,
			pending: packages.pending,
			active: null,
			closed: [...packages.closed, packages.active]
		}
	}

	/**
	 * Calculates the number and size of packages based on the total steps
	 * @param totalSteps
	 * @returns the calculated array
	 */
	public static generatePendingPackageStructure(totalSteps: number): OrcadentPackage[] {
		if(totalSteps < 1) {
			return [];
		}

		const result: OrcadentPackage[] = [];

		while (totalSteps > 0) {
			const steps = Math.min(ProductStepLimit.ORCADENT_PACKAGE_SIZE, totalSteps);
			result.push({steps: steps, type: OrcadentPackageType.PLANNED});
			totalSteps -= steps;
		}

		return result;
	}

	public static getProductKeyForAmountOfSteps( totalSteps: number) : ProductKey {
		const mediumStepRange = ProductInfoForKey[ProductKey.ORCADENT_SCHIENE_MEDIUM].stepRange;
		const complexStepRange = ProductInfoForKey[ProductKey.ORCADENT_SCHIENE_COMPLEX].stepRange;
		const unlimitedStepRange =
			ProductInfoForKey[ProductKey.ORCADENT_SCHIENE_UNLIMITED].stepRange;
		if (totalSteps >= mediumStepRange.min && totalSteps <= mediumStepRange.max) {
			return ProductKey.ORCADENT_SCHIENE_MEDIUM;
		} else if (totalSteps >= complexStepRange.min && totalSteps <= complexStepRange.max) {
			return ProductKey.ORCADENT_SCHIENE_COMPLEX;
		} else if (totalSteps >= unlimitedStepRange.min && totalSteps <= unlimitedStepRange.max) {
			return ProductKey.ORCADENT_SCHIENE_UNLIMITED;
		}
		return ProductKey.ORCADENT_SCHIENE_ASSESSMENT;
	}
}
